import {
  FamilyApplicationEndpoints,
  GetTaxCommissionIncomeDetailsRespSchema,
  SetApplicationFlag
} from '@/apis/family_application/requests'
import { LogUserActivity } from '@/apis/users/requests'
import { useAppAlert } from '@/app/AppAlertProvider'
import { Button, buttonVariants } from '@/app/common/components/Button'
import LoadingCard from '@/app/common/components/LoadingCard'
import { WarningIcon } from '@/app/common/icons/support'
import { parseRequestError } from '@/app/common/utilities'
import axiosInstance from '@/libs/axios_config'
import { AppRoutePaths } from '@/routing/route_paths'
import {
  selectFamilyApplication,
  setTaxCommissionResponse,
} from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { LOG_TYPES } from '../../constants'
import FamilyWizardLayout from '../../FamilyWizardLayout'
import { SkipVerification } from '../skip-verification/SkipVerification'
import { hasTaxCommissionErrors } from './tax-commission.utils'

function TaxCommissionSetupPage() {
  const application = useSelector(selectFamilyApplication)
  const programId = useSelector(selectProgramID)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [skip, setSkip] = useState(false)
  const { fireAlert } = useAppAlert()
  const dispatch = useDispatch()

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'You must complete the Tax comission income page',
      })
      hasLoggedPageView.current = true;
    }
  }, []) 

  const { status, mutate, data } = useMutation({
    onError: async (error) => {
      await SetApplicationFlag({
        application_id: application.application_id,
        flag: 'tax_commission_error',
        global_application_step_code: 'tax_commission',
      })

      fireAlert({
        variant: 'error',
        message: parseRequestError(error).message,
      }).then((response) => {
        if (response.confirmed) {
          navigate(
            AppRoutePaths.family_application.income_verification
              .document_instructions
          )
        }
      })
    },
    onSuccess: async (resp) => {
      dispatch(setTaxCommissionResponse(resp))
      if (hasTaxCommissionErrors(resp)) {
        await SetApplicationFlag({
          application_id: application.application_id,
          flag: 'tax_commission_returned_data_does_not_match',
          global_application_step_code: 'tax_commission',
        })
      } else {
        navigate(
          AppRoutePaths.family_application.income_verification
            .tax_commission_attestation
        )
      }
    },
    mutationFn: async () => {
      const response = await axiosInstance.post(
        FamilyApplicationEndpoints.get_family_income_tax_commission,
        {
          application_id: application.application_id,
          program_id: programId,
          include_spouse: application.household_filed_federal_income === "individually"
        }
      )

      return GetTaxCommissionIncomeDetailsRespSchema.parse(response.data)
    },
  })

  const hasLoggedViewUnableToVerify = useRef(false);
  useEffect(() => {
    if (!hasLoggedViewUnableToVerify.current && (status === 'success' && hasTaxCommissionErrors(data))) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Tax comission unable to verify',
      })
      hasLoggedViewUnableToVerify.current = true;
    }
  }, [status, data]) 


  if (skip) {
    return <SkipVerification onCancel={() => setSkip(false)} />
  }

  if (status === 'success' && hasTaxCommissionErrors(data)) {
    return (
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          <h1>Tax Commission Unable to Verify Income</h1>
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content className="space-y-4">
          <WarningIcon className="text-red-500 text-4xl mx-auto" />
          <p className="text-gray-700  mb-4 text-lg">
            Unfortunately, the system failed to return data from the Utah Tax Commission to verify your income.
          </p>
          <p className="text-gray-700 text-sm mb-4">
            Verify income manually using standard tax documents such as a
            completed Federal Income Tax return.
          </p>
          <div className="text-gray-700 ml-4 text-sm ">
            {data.map((data) => {

              return (
                <ul>
                  {data.errors.map((data) => (
                    <li className="list-disc my-1">{data.message}</li>
                  ))}
                </ul>
              )
            })}
          </div>
        </FamilyWizardLayout.Content>
        <FamilyWizardLayout.ActionBar className="flex-col">
          <Link
            className={buttonVariants({
              variant: 'primary',
              className: 'h-10',
            })}
            to={
              AppRoutePaths.family_application.income_verification
                .capture_instructions
            }
          >
            {t('continue')}
          </Link>
          <Button
            className="h-10"
            variant={'text'}
            size={'text'}
            onClick={() => setSkip(true)}
          >
            {t('skip_income_verification')}
          </Button>
        </FamilyWizardLayout.ActionBar>
      </FamilyWizardLayout>
    )
  }

  if (status === 'pending') {
    return (
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          {t('tax_commission_steps_needed')}
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content>
          <LoadingCard isLoading={true}>{t('processing')}</LoadingCard>
        </FamilyWizardLayout.Content>
      </FamilyWizardLayout>
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        {t('tax_commission_steps_needed')}
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="mt-10 h-full">
        <WarningIcon className="text-4xl mx-auto mb-4 mt-6 text-center text-orange-500" />
        <div className="text-gray-700 space-y-2">
          <p>{t('to_verify_utah_income_info')}</p>
          <p>{t('utah_disclaimer_verify')}</p>
        </div>
      </FamilyWizardLayout.Content>

      <FamilyWizardLayout.ActionBar className="flex-col">
        <Button onClick={() => mutate()} className="w-full">
          {t('tax_commission_process_completed')}
        </Button>
        <Link
          className={buttonVariants({
            variant: 'text',
            size: 'text',
            className: 'h-12',
          })}
          to={
            AppRoutePaths.family_application.income_verification
              .document_instructions
          }
        >
          {t('skip_tax_commission_for_now')}
        </Link>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}

export default TaxCommissionSetupPage
